import {yupResolver} from "@hookform/resolvers/yup";
import React, {useEffect} from "react";
import {FieldErrors, FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {STATUS_PAGE_SYSTEM_METRIC_URL} from "@/lib/links.ts";
import {ServerErrors} from "@/lib/types/common.ts";
import {Metric} from "@/lib/types/statuspages.ts";
import {ServiceSelectField} from "@/pages/checks/components/form/fields/ServiceSelectField.tsx";

export interface StatusPageSystemMetricFormValues {
  id?: number;
  name: string;
  is_visible: boolean;
  service?: number;
}

export type FormValuesKey = keyof StatusPageSystemMetricFormValues;

const StatusPageSystemMetricFormSchema = Yup.object({
  id: Yup.number(),
  name: Yup.string().required("This field is required"),
  service: Yup.number().nullable().required("This field is required"),
  is_visible: Yup.boolean().default(true).required(),
});

interface SystemMetricFormProps {
  metric?: Metric;
  onSubmit: (d: StatusPageSystemMetricFormValues) => void;
  serverErrors?: ServerErrors;
  onError?: (errors: FieldErrors<StatusPageSystemMetricFormValues>, event?: React.BaseSyntheticEvent) => void;
  id?: string;
}

const StatusPageSystemMetricForm = ({id, metric, serverErrors, onSubmit, onError}: SystemMetricFormProps) => {
  const methods = useForm<StatusPageSystemMetricFormValues>({
    resolver: yupResolver<StatusPageSystemMetricFormValues>(StatusPageSystemMetricFormSchema),
    defaultValues: {
      id: metric?.id,
      is_visible: metric ? metric.is_visible : true,
      service: metric?.service,
      name: metric?.name,
    },
  });

  const {handleSubmit, register, setValue, setError, watch} = methods;

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => {
          setError(key as FormValuesKey, {message: serverErrors[key][0]});
        });
    }
  }, [setError, serverErrors]);

  return (
    <FormProvider {...methods}>
      <form id={id} onSubmit={handleSubmit(onSubmit, onError)}>
        {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}
        <div className={"d-flex flex-column gap-4"}>
          <div className="pulseTextBody">
            System metrics, such as response time graphs, add visibility into critical system performance on your public
            Status Page.&nbsp;
            <a href={STATUS_PAGE_SYSTEM_METRIC_URL} target={"_blank"}>
              View Documentation
            </a>
          </div>
          {metric && <input type="number" hidden {...register("id")} />}
          <ServiceSelectField
            name={"service"}
            label={"Associated Check"}
            tooltip={"Link this System Metric to a Check to display response time graph."}
            required
            isClearable={!metric}
            defaultValue={
              metric
                ? {
                    id: metric.service,
                    name: metric.service_name,
                    type: metric.service_type,
                    address: "",
                  }
                : undefined
            }
            onChange={(v) => {
              setValue("service", v?.id);
              if (v?.name && !watch("name")) {
                setValue("name", v.name);
              }
            }}
          />
          <InputField name={"name"} label={"Metric Display Name"} required />
          <SwitchField name={"is_visible"} label={"Display this Metric in the System Metrics section of the page"} />
        </div>
      </form>
    </FormProvider>
  );
};

export default StatusPageSystemMetricForm;
